import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useToast } from './useToast';
import { badgeQueryKeys, useUserBadges } from './useBadgeQueries';

interface BadgeNotification {
  badgeId: string;
  badgeName: string;
  category: string;
  earnedAt: string;
}

// Hook to handle badge achievement notifications
export function useBadgeNotifications() {
  const { user } = useAuth();
  const { achievement } = useToast();
  const queryClient = useQueryClient();

  // Track the last check time in localStorage
  const getLastCheckTime = useCallback((): string | null => {
    if (!user?.id) return null;
    return localStorage.getItem(`badge-last-check-${user.id}`);
  }, [user?.id]);

  const setLastCheckTime = useCallback(
    (time: string) => {
      if (!user?.id) return;
      localStorage.setItem(`badge-last-check-${user.id}`, time);
    },
    [user?.id]
  );

  const showBadgeNotification = useCallback(
    (notification: BadgeNotification) => {
      const categoryEmojis = {
        plant: '🌱',
        achievement: '🏆',
        milestone: '⭐',
      };

      const emoji = categoryEmojis[notification.category as keyof typeof categoryEmojis] || '🏅';

      achievement(`${emoji} Badge Earned!`, notification.badgeName, {
        duration: 5000, // Show for 5 seconds
        action: {
          label: 'View Badges',
          onClick: () => {
            // Navigate to badges page
            window.location.href = '/badges';
          },
        },
      });
    },
    [achievement]
  );

  // Get user badges with polling for notifications
  const { data: badgeData, isSuccess } = useUserBadges(user?.id || '', !!user?.id, {
    refetchInterval: 30000, // Check every 30 seconds
    staleTime: 10000, // Allow more frequent updates for notifications
  });

  // Check for new badges and show notifications
  useEffect(() => {
    if (!isSuccess || !badgeData || !user?.id) return;

    const lastCheckTime = getLastCheckTime();
    const currentTime = new Date().toISOString();

    // If this is the first check, just record the time without notifications
    if (!lastCheckTime) {
      setLastCheckTime(currentTime);
      return;
    }

    // Find badges earned since last check
    const newBadges = (badgeData?.badges || []).filter(badge => {
      if (!badge.earnedAt) return false;
      const earnedTime = new Date(badge.earnedAt);
      const lastCheck = new Date(lastCheckTime);
      return earnedTime > lastCheck;
    });

    // Show notifications for new badges
    newBadges.forEach(badge => {
      showBadgeNotification({
        badgeId: badge.id,
        badgeName: badge.name,
        category: badge.category,
        earnedAt: badge.earnedAt || currentTime,
      });
    });

    // Update last check time
    if (newBadges.length > 0) {
      setLastCheckTime(currentTime);
    }
  }, [isSuccess, badgeData, user?.id, getLastCheckTime, setLastCheckTime, showBadgeNotification]);

  // Manual trigger for testing or immediate evaluation
  const triggerBadgeCheck = useCallback(async () => {
    if (!user?.id) return;

    // Invalidate and refetch badge data
    await queryClient.invalidateQueries({
      queryKey: badgeQueryKeys.userBadges(user.id),
    });

    // Reset last check time to show notifications for recently earned badges
    const oneMinuteAgo = new Date(Date.now() - 60000).toISOString();
    setLastCheckTime(oneMinuteAgo);
  }, [user?.id, queryClient, setLastCheckTime]);

  return {
    triggerBadgeCheck,
    showBadgeNotification,
  };
}

// Component to automatically enable notifications (add to your main App component)
export function BadgeNotificationProvider({ children }: { children: React.ReactNode }) {
  useBadgeNotifications();
  return <>{children}</>;
}
